/* The agent's material follows the newest message. Healthy drafts have one entry in the reply
 * box; the questions and recovery cards retain their own controls. Access gates every read.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useId, useState } from "react";
import type { z } from "zod";
import { dependenciesSatisfied } from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import type { InboxAgentState } from "../../src/inbox-v2/agent-contract.ts";
import { draftEmail } from "../../src/mail-content.ts";
import { fetchReplyView } from "../compose.tsx";
import { ConversationRead, type Redraft, useRedraft, useWorkBusy } from "../draft-again.tsx";
import { SLOTS_WAIT_LINE, useSlotsWaiting } from "../draft-slots.tsx";
import { LeadDrafting } from "../drafting-switches.tsx";
import { EarlierConversationFold } from "../earlier-conversation.tsx";
import { AgentIcon, ClockIcon } from "../icons.tsx";
import type { StartingDraft } from "../inbox-v2/reply-box.tsx";
import type { ReplyBoxLink } from "../inbox-v2/reply-box-link.ts";
import { AppLink, campaignPath, usePlanApi } from "../workspace-context.tsx";
import { AgentChip } from "./agent-chip.tsx";
import { refreshAgentReads } from "./agent-reads.ts";
import { approveFromBox, saveBoxDraft } from "./approve-from-box.ts";
import {
  newestPipelineDraft,
  parseEmail,
  useEmailDisplays,
  whenLabel,
} from "./conversation-shared.ts";
import { DetailSchema, EventsSchema } from "./detail-schema.ts";
import { draftAids } from "./draft-aids.ts";
import { request } from "./page-request.ts";
import { useAgentDetail, useAgentPolling, workInFlight } from "./use-agent.ts";
import { Activity, type DraftBox, WorkPane } from "./work-pane.tsx";
import "./agent-item.css";

type Detail = z.infer<typeof DetailSchema>;

/**
 * What the thread places: the item after the newest message, the reply box's starting draft, and why
 * the box offers none where the draft's commitments wait on the card.
 */
export type AgentParts = { item: ReactNode; draft: StartingDraft | null; waits: string | null };

/**
 * The item's head: whose material this is, its state in the row's words, this lead's drafting as a
 * quiet line beside it (`children`, step 10, round 3), and who sees it.
 */
function AgentHead(props: { state: InboxAgentState | null; children?: ReactNode }) {
  return (
    <header className="lcr-agent-head">
      <span className="lcr-agent-name">
        <AgentIcon size={16} />
        Reply agent
      </span>
      {props.state && props.state !== "draft_ready" && (
        <AgentChip state={props.state} named={false} />
      )}
      {props.children}
      <span className="lcr-agent-rule" aria-hidden="true" />
      {/* Q7 of the shell plan, still open for Dawid: the words for who sees this. */}
      <span className="lcr-agent-caption">Only people with the reply agent see this</span>
    </header>
  );
}

/**
 * A draft is on screen: "New drafts off" beside "Draft ready" would only contradict it, so the head
 * says drafting is held only while there is no draft (LEA-225, W5). The switch itself stays while on.
 */
const hasOpenDraft = (plan: Detail["plan"]): boolean =>
  plan.operatorOutcome === null &&
  plan.steps.some(
    (step) => step.input.kind === "reply" && ["pending", "approved"].includes(step.state),
  );

/** Before the plan is in hand: the head at once, the card's shape after a beat, or why it failed. */
function AgentPending(props: { failed: boolean; onRetry: () => void }) {
  return (
    <section className="lcr-agent-item" aria-label="Reply agent" aria-busy={!props.failed}>
      <AgentHead state={null} />
      {props.failed ? (
        <p className="lcr-agent-failed" role="alert">
          The reply agent's work could not be loaded.{" "}
          <button type="button" className="lcr-linklike" onClick={props.onRetry}>
            Try again
          </button>
        </p>
      ) : (
        <>
          <p className="lcr-visually-hidden" role="status">
            Loading the reply agent's work
          </p>
          <div className="lcr-opening" aria-hidden="true">
            <div className="lcr-sheet lcr-opening-card">
              <span className="lcr-skeleton" data-width="heading" />
              <span className="lcr-skeleton" data-width="line-1" />
              <span className="lcr-skeleton" data-width="line-3" />
              <div className="lcr-opening-actions">
                <span className="lcr-skeleton" data-width="action" />
                <span className="lcr-skeleton" data-width="action" />
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** The conversation's recorded events, as Activity and the cards read them. */
function useEvents(plan: Detail["plan"]) {
  const planApi = usePlanApi(plan.id);
  return useQuery({
    queryKey: ["events", plan.id, plan.revision],
    queryFn: async () => EventsSchema.parse(await request(`${planApi}/events`)),
  });
}

/**
 * Activity, folded until it is asked for. Its Show activity ends the agent's line with the other
 * quiet actions (LEA-225), and what it opens follows the cards.
 */
function useActivityFold(props: {
  detail: Detail;
  viewerId: string;
  events: ReturnType<typeof useEvents>;
}) {
  const [activity, setActivity] = useState(false);
  const activityId = useId();
  return {
    toggle: (
      <button
        type="button"
        className="lcr-small-button lcr-ghost lcr-agent-activity-toggle"
        aria-expanded={activity}
        aria-controls={activity ? activityId : undefined}
        onClick={() => setActivity(!activity)}
      >
        <ClockIcon size={16} />
        <span>{activity ? "Hide activity" : "Show activity"}</span>
      </button>
    ),
    panel: activity && (
      <div className="lcr-agent-activity" id={activityId}>
        <Activity
          plan={props.detail.plan}
          drafts={props.detail.drafts}
          viewerId={props.viewerId}
          events={props.events}
        />
      </div>
    ),
  };
}

/** The item itself: the head, the reply agent's cards and its one line, then Activity when asked. */
function AgentItem(props: {
  detail: Detail;
  state: InboxAgentState | null;
  viewerId: string;
  onChange: () => Promise<void>;
  onEditing: (editing: boolean) => void;
  editing: boolean;
  redraft: Redraft;
  link: ReplyBoxLink;
}) {
  const { detail, link } = props;
  const { plan } = detail;
  const planApi = usePlanApi(plan.id);
  const [error, setError] = useState("");
  const events = useEvents(plan);
  const reply = useQuery({
    queryKey: ["reply", plan.id, plan.revision],
    queryFn: () => fetchReplyView(planApi),
  });
  const displays = useEmailDisplays(detail.thread);
  const activity = useActivityFold({ detail, viewerId: props.viewerId, events });
  // The reply box owns the draft's text here: the card loads it there, and approves what it holds.
  const box: DraftBox = {
    link,
    sends: true,
    approve: async (step, draft) => {
      const pending = link.begin(plan.id);
      try {
        await approveFromBox({
          planApi,
          plan,
          step,
          draft,
          box: pending.content,
          parse: parseEmail,
          onSaved: pending.saved,
        });
      } catch (cause) {
        // A save may have succeeded. Read it back before a retry, keeping the original refusal.
        await props.onChange();
        throw cause;
      } finally {
        pending.release();
      }
    },
  };
  return (
    <section className="lcr-agent-item" aria-label="Reply agent">
      {/* The old header's switch and fold (step 10, C2b): this lead's drafting, and a follow-up's
          earlier campaign, both read only by people with the agent, so they sit with its work. */}
      <AgentHead state={props.state}>
        {!(detail.drafting?.heldBy && hasOpenDraft(plan)) && (
          <LeadDrafting
            planId={plan.id}
            decision={detail.drafting}
            viewerId={props.viewerId}
            when={whenLabel}
            campaignLink={
              <AppLink href={campaignPath(plan.workspaceId, plan.campaignId)}>
                its campaign's page
              </AppLink>
            }
          />
        )}
      </AgentHead>
      {detail.earlierConversation && (
        <EarlierConversationFold conversation={detail.earlierConversation} />
      )}
      {error && (
        <p className="lcr-agent-failed" role="alert">
          {error}
        </p>
      )}
      <WorkPane
        detail={detail}
        viewerId={props.viewerId}
        events={events}
        reply={reply.data ?? null}
        writing={false}
        displays={displays}
        redraft={props.redraft}
        editing={props.editing}
        onChange={props.onChange}
        onError={setError}
        onEditing={props.onEditing}
        box={box}
        extra={activity.toggle}
      />
      {activity.panel}
    </section>
  );
}

/** The plan in hand: the state both the item and the reply box read. */
/**
 * The agent's material for one conversation, from before its plan is read until after: one
 * component throughout, so the thread it hands its parts to, and the reply box in it, keep what
 * they hold when the plan lands. A rep who opened the box and typed while the plan was on its way
 * kept neither when this was two components (browser send journeys, run 39171021862).
 */
function AgentLoaded(props: {
  planId: string;
  /** Absent while the plan is still being read; `failed` says whether that read failed. */
  detail: Detail | undefined;
  failed: boolean;
  onRetry: () => void;
  workspaceId: string;
  state: InboxAgentState | null;
  viewerId: string;
  link: ReplyBoxLink;
  onUnsaved: (dirty: boolean) => void;
  children: (agent: AgentParts) => ReactNode;
}) {
  const { detail, planId, workspaceId, onUnsaved } = props;
  const cache = useQueryClient();
  const planApi = usePlanApi(planId);
  const [editing, setEditing] = useState(false);
  // The rep acted on a card: the plan, and the rows' states, are read again.
  const onChange = useCallback(async () => {
    await Promise.all([
      cache.invalidateQueries({ queryKey: ["plan", workspaceId, planId] }),
      refreshAgentReads(cache, workspaceId),
    ]);
  }, [cache, workspaceId, planId]);
  // The box changed the ending: the saved revision is read now, to be put in the box whole.
  const refresh = useCallback(async () => {
    const fresh = DetailSchema.parse(await request(planApi));
    cache.setQueryData(["plan", workspaceId, planId], fresh);
    void refreshAgentReads(cache, workspaceId);
    return fresh;
  }, [cache, planApi, workspaceId, planId]);
  const redraft = useRedraft(planId, detail, onChange, editing);
  const moving = redraft !== null && (redraft.flight !== null || workInFlight(props.state, detail));
  useAgentPolling(workspaceId, planId, moving);
  // A draft editor holding unsaved text guards leaving the conversation, as the reply box does.
  useEffect(() => onUnsaved(editing), [editing, onUnsaved]);
  useEffect(() => () => onUnsaved(false), [onUnsaved]);
  // A draft that Done closed is not offered to the box; Reopen draft brings it back (LEA-225).
  const offered =
    detail && redraft?.offersDraft && !detail.plan.operatorOutcome
      ? newestPipelineDraft(detail.plan, detail.drafts)
      : null;
  // Its text holds commitments not yet confirmed: the box offers it only once the card has them.
  const waiting = useSlotsWaiting(planId, offered?.revision ?? null);
  const draft = waiting ? null : offered;
  // A replacement request holds loading and sending while leaving existing words editable.
  const [busy] = useWorkBusy(planId);
  const step = detail?.plan.steps.find(
    (step) => step.input.kind === "reply" && step.input.draftRevision === draft?.revision,
  );
  if (!detail || !redraft)
    return props.children({
      item: <AgentPending failed={props.failed} onRetry={props.onRetry} />,
      draft: null,
      waits: null,
    });
  return props.children({
    item: (
      <AgentItem
        detail={detail}
        state={props.state}
        viewerId={props.viewerId}
        onChange={onChange}
        onEditing={setEditing}
        editing={editing}
        redraft={redraft}
        link={props.link}
      />
    ),
    // The whole email, ending included: the reply box sends what it holds and adds nothing.
    draft: draft && {
      revision: draft.revision,
      ...draftEmail(draft),
      aids: draftAids({ detail, draft, planApi, link: props.link, refresh, onChange }),
      held: busy,
      sendBlocked: busy || !step || !dependenciesSatisfied(detail.plan, step),
      prepareSend: async () => {
        if (!step) throw new Error("The draft changed. Load its latest version before sending.");
        const pending = props.link.begin(planId);
        try {
          if (pending.content?.loaded !== draft.revision)
            throw new Error("The draft changed. Load its latest version before sending.");
          return await saveBoxDraft({
            planApi,
            plan: detail.plan,
            step,
            draft,
            box: pending.content,
            parse: parseEmail,
            onSaved: pending.saved,
            exactFormatted: true,
          });
        } catch (cause) {
          await onChange();
          throw cause;
        } finally {
          pending.release();
        }
      },
    },
    waits: waiting ? SLOTS_WAIT_LINE : null,
  });
}

/**
 * The agent's material for one open conversation, handed to `children` to place: the timeline item
 * and the reply box's starting draft, or null with the switch off, when nothing is asked at all.
 */
export function AgentMaterial(props: {
  workspaceId: string;
  planId: string;
  /** The person's reply-agent switch (`/access`). */
  enabled: boolean;
  /** The row's state from the list (`inbox/agent`), when the open row is on the page. */
  state: InboxAgentState | null;
  viewerId: string;
  /** The open conversation's reply box, which owns the draft's text (`reply-box-link.ts`). */
  link: ReplyBoxLink;
  onUnsaved: (dirty: boolean) => void;
  children: (agent: AgentParts | null) => ReactNode;
}) {
  const detail = useAgentDetail(props.workspaceId, props.planId, props.enabled);
  if (!props.enabled) return props.children(null);
  return (
    <ConversationRead value={detail.isFetchedAfterMount}>
      {/* Keyed by conversation: an edit or a fold open in one never carries into the next. */}
      <AgentLoaded
        key={props.planId}
        planId={props.planId}
        detail={detail.data}
        failed={detail.isError}
        onRetry={() => void detail.refetch()}
        workspaceId={props.workspaceId}
        state={props.state}
        viewerId={props.viewerId}
        link={props.link}
        onUnsaved={props.onUnsaved}
      >
        {props.children}
      </AgentLoaded>
    </ConversationRead>
  );
}
